import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, FileText, Wallet, CreditCard } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('eventism_token')}` } });
const usd = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function MyInvoicesPage() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = useCallback(() => {
    axios.get(`${API}/me/platform-invoices`, auth())
      .then(r => setData(r.data))
      .catch(e => toast.error(e.response?.data?.detail || 'Could not load your invoices'));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const sid = params.get('session_id');
    if (!sid) return;
    axios.get(`${API}/platform-invoices/topup/confirm`, { ...auth(), params: { session_id: sid } })
      .then(r => {
        if (r.data.credited || r.data.already_credited) {
          toast.success(`Wallet topped up — balance ${usd(r.data.balance_cents)}. You can pay the invoice now.`);
          load();
        }
      })
      .catch(e => toast.error(e.response?.data?.detail || 'Could not confirm top-up'))
      .finally(() => setParams({}, { replace: true }));
  }, [params, setParams, load]);

  const pay = async (inv) => {
    if (!window.confirm(`Pay ${usd(inv.amount_cents)} for ${inv.number} from your Eventism wallet?`)) return;
    setBusy(inv.invoice_id);
    try {
      const r = await axios.post(`${API}/platform-invoices/${inv.invoice_id}/pay`, {}, auth());
      toast.success(`${r.data.number} paid — thank you!`);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.reason === 'insufficient_funds') {
        toast.error(`Not enough in your wallet — you're ${usd(d.shortfall_cents)} short. Use "Top up & pay".`);
      } else {
        toast.error(typeof d === 'string' ? d : 'Payment failed');
      }
    } finally { setBusy(null); }
  };

  const topup = async (inv) => {
    setBusy(inv.invoice_id);
    try {
      const r = await axios.post(`${API}/platform-invoices/${inv.invoice_id}/topup`, {}, auth());
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Could not start top-up');
      setBusy(null);
    }
  };

  if (!data) return <div className="min-h-screen bg-background pt-28 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>;

  const open = data.invoices.filter(i => i.status === 'open');
  const done = data.invoices.filter(i => i.status !== 'open');
  const totalBal = (data.sources.org?.balance_cents || 0) + (data.sources.user?.balance_cents || 0);

  return (
    <div className="min-h-screen bg-background pt-24 pb-16">
      <div className="max-w-3xl mx-auto px-4 space-y-5" data-testid="my-invoices-page">
        <div>
          <h1 className="text-2xl font-extrabold flex items-center gap-2"><FileText className="h-6 w-6 text-amber-400" /> My Invoices</h1>
          <p className="text-sm text-muted-foreground">Bills from Eventism — paid straight from your Eventism wallet.</p>
        </div>

        <div className="flex flex-wrap gap-2">
          {data.sources.org && (
            <span className="text-xs px-3 py-1.5 rounded-full border border-border flex items-center gap-1.5" data-testid="org-wallet-balance">
              <Wallet className="h-3.5 w-3.5 text-indigo-400" /> {data.sources.org.name}: <strong>{usd(data.sources.org.balance_cents)}</strong>
            </span>
          )}
          <span className="text-xs px-3 py-1.5 rounded-full border border-border flex items-center gap-1.5" data-testid="user-wallet-balance">
            <Wallet className="h-3.5 w-3.5 text-emerald-400" /> Personal wallet: <strong>{usd(data.sources.user.balance_cents)}</strong>
          </span>
        </div>

        {open.length === 0 ? (
          <Card className="border-border"><CardContent className="py-8 text-center text-sm text-muted-foreground" data-testid="no-open-invoices">Nothing due — you're all settled up. 🎉</CardContent></Card>
        ) : open.map(inv => {
          const covered = totalBal >= inv.amount_cents || (data.sources.org?.balance_cents || 0) >= inv.amount_cents || data.sources.user.balance_cents >= inv.amount_cents;
          const canCover = (data.sources.org?.balance_cents || 0) >= inv.amount_cents || data.sources.user.balance_cents >= inv.amount_cents;
          return (
            <Card key={inv.invoice_id} className="border-amber-500/30" data-testid={`invoice-card-${inv.number}`}>
              <CardContent className="py-4 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <p className="text-xs font-mono text-muted-foreground">{inv.number}{inv.due_date ? ` · due ${inv.due_date}` : ''}</p>
                  <p className="text-sm font-semibold">{inv.description}</p>
                  <p className="text-xl font-extrabold text-amber-400">{usd(inv.amount_cents)}</p>
                </div>
                <div className="flex gap-2">
                  {canCover ? (
                    <Button onClick={() => pay(inv)} disabled={busy === inv.invoice_id} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid={`pay-invoice-${inv.number}`}>
                      {busy === inv.invoice_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Pay from wallet</>}
                    </Button>
                  ) : (
                    <Button onClick={() => topup(inv)} disabled={busy === inv.invoice_id} data-testid={`topup-invoice-${inv.number}`}>
                      {busy === inv.invoice_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CreditCard className="h-4 w-4 mr-1.5" /> Top up &amp; pay</>}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}

        {done.length > 0 && (
          <div>
            <p className="text-sm font-semibold mb-2">History</p>
            <div className="rounded-lg border border-border overflow-x-auto">
              <table className="w-full text-sm" data-testid="invoice-history-table">
                <thead><tr className="bg-muted/40 text-left"><th className="px-3 py-2">#</th><th className="px-3 py-2">For</th><th className="px-3 py-2 text-right">Amount</th><th className="px-3 py-2">Status</th></tr></thead>
                <tbody>
                  {done.map(inv => (
                    <tr key={inv.invoice_id} className="border-t border-border/60">
                      <td className="px-3 py-1.5 font-mono text-xs">{inv.number}</td>
                      <td className="px-3 py-1.5 text-xs">{inv.description}</td>
                      <td className="px-3 py-1.5 text-right font-semibold">{usd(inv.amount_cents)}</td>
                      <td className="px-3 py-1.5 text-xs capitalize">{inv.status === 'paid' ? <span className="text-emerald-400">paid {inv.paid_at ? new Date(inv.paid_at).toLocaleDateString() : ''}</span> : inv.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
